// The availability pipeline, one platform at a time: every cart run's way from scrape to
// Reach, the build that took it there, its Reach health by city, the days the backfill
// closed, and a rebuild of past days.
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  getPipelineV1AvailabilityPipelineGetQueryKey,
  useDaysV1AvailabilityDaysGet,
  useGetRunV1RunsRunIdGet,
  useHealthV1AvailabilityHealthGet,
  useListJobsV1JobsGet,
  usePipelineV1AvailabilityPipelineGet,
  useRebuildV1AvailabilityRebuildPost,
  useRunNowV1JobsJobIdRunsPost,
} from "../api/generated/qcsControlPlane";
import type {
  AvailabilityPlatform,
  AvailabilityStageOut,
  JobLastRun,
  Platform,
  RebuildMode,
} from "../api/generated/model";
import {
  type CartProgress,
  ago,
  fmtDate,
  fmtDuration,
  fmtInt,
  fmtRows,
  fmtTime,
  platformLabel,
  statusTone,
  stepLabel,
  stepsInOrder,
} from "../api/labels";
import { platformKeys } from "../api/platforms";
import { useCanWrite } from "../auth/me";
import { useToast } from "../components/toast";
import { Seg, Stat } from "../components/foundation";
import { toneVariant } from "../lib/badge-tone";
import {
  Badge,
  Button,
  Dot,
  EmptyState,
  ErrorState,
  FormField,
  Grid,
  Input,
  NativeSelect,
  PageHeader,
  Panel,
  Row,
  Stack,
} from "@akaike/ui";
import { type StageState, cityShares, delaySeconds, platformState, stageState } from "./availability-state";

const STATE_LABEL: Record<StageState["kind"], string> = {
  scraping: "scraping",
  built: "Reach built",
  failed: "failed",
  late: "late",
  waiting: "waiting",
};

const STATE_TONE = {
  scraping: "info",
  built: "ok",
  failed: "crit",
  late: "warn",
  waiting: "idle",
} as const satisfies Record<StageState["kind"], string>;

const MODES: { value: RebuildMode; label: string; hint: string }[] = [
  { value: "rollup", label: "Roll up", hint: "Re-aggregate each day's daily metrics." },
  { value: "close", label: "Close", hint: "Close each day again, then rebuild its runs' Reach." },
  { value: "reclose", label: "Reclose", hint: "Close only the days a late run reopened." },
];

const pct = (share: number | null) => (share === null ? "-" : `${(share * 100).toFixed(1)}%`);

export function Availability() {
  const jobsQuery = useListJobsV1JobsGet();
  // In the registry's order, as every other platform picker lists them.
  const platforms = useMemo(() => {
    const built = new Set(
      (jobsQuery.data ?? []).filter((j) => j.flow === "availability_build").map((j) => j.platform),
    );
    return platformKeys().filter((p) => built.has(p));
  }, [jobsQuery.data]);
  const [picked, setPicked] = useState<Platform | null>(null);
  const platform = picked ?? platforms[0] ?? null;

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Availability"
        description={
          <>
            Scrape, enrich, roll up and Reach, for every cart run. One writer per platform, on the{" "}
            <span className="mono">qcs-availability</span> queue.
          </>
        }
      />
      {jobsQuery.isError ? (
        <Panel>
          <Panel.Body>
            <ErrorState compact title="Could not load jobs" message="Is the control plane up?" onRetry={() => jobsQuery.refetch()} />
          </Panel.Body>
        </Panel>
      ) : platform === null ? (
        <Panel>
          <Panel.Body>
            <EmptyState compact title={jobsQuery.isLoading ? "Loading" : "No availability jobs yet"}>
              The control plane creates a build and a backfill job for each platform when it starts.
            </EmptyState>
          </Panel.Body>
        </Panel>
      ) : (
        <PlatformPipeline
          platform={platform}
          tabs={<PlatformTabs platforms={platforms} value={platform} onChange={setPicked} />}
        />
      )}
    </>
  );
}

function PlatformTabs({
  platforms,
  value,
  onChange,
}: {
  platforms: Platform[];
  value: Platform;
  onChange: (p: Platform) => void;
}) {
  return (
    <Seg
      brand
      value={value}
      onChange={onChange}
      options={platforms.map((p) => ({ value: p, label: platformLabel(p) }))}
    />
  );
}

function PlatformPipeline({ platform, tabs }: { platform: Platform; tabs: React.ReactNode }) {
  const qc = useQueryClient();
  const notify = useToast();
  const canWrite = useCanWrite();
  const pipelineQuery = usePipelineV1AvailabilityPipelineGet(
    { platform, limit: 48 },
    { query: { refetchInterval: 10_000 } },
  );
  const daysQuery = useDaysV1AvailabilityDaysGet({ platform, limit: 14 }, { query: { refetchInterval: 60_000 } });
  const [selected, setSelected] = useState<string | null>(null);
  const runNow = useRunNowV1JobsJobIdRunsPost<Error>({
    mutation: {
      onSuccess: () => {
        notify("Build queued");
        void qc.invalidateQueries({ queryKey: getPipelineV1AvailabilityPipelineGetQueryKey({ platform, limit: 48 }) });
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const data = pipelineQuery.data;
  if (pipelineQuery.isError) {
    return (
      <Stack gap="md">
        {tabs}
        <Panel>
          <Panel.Body>
            <ErrorState compact title="Could not load the pipeline" message="Is the control plane up?" onRetry={() => pipelineQuery.refetch()} />
          </Panel.Body>
        </Panel>
      </Stack>
    );
  }
  if (!data) {
    return (
      <Stack gap="md">
        {tabs}
        <span className="faint">Loading...</span>
      </Stack>
    );
  }
  const now = new Date();
  const lag = data.lag_threshold_minutes;
  const worst = platformState(data.stages, now, lag);
  const chosen = data.stages.find((s) => s.run_id === selected) ?? data.stages[0] ?? null;
  const retry = () => runNow.mutate({ jobId: data.build_job_id });

  return (
    <Stack gap="md">
      <Row justify="between" className="flex-wrap">
        <Row gap="sm">
          {tabs}
          {worst !== null && <Badge variant={toneVariant(STATE_TONE[worst])}>{STATE_LABEL[worst]}</Badge>}
        </Row>
        {canWrite && (
          <Button variant="primary" disabled={!data.build_enabled} loading={runNow.isPending} onClick={retry}>
            Run build now
          </Button>
        )}
      </Row>

      <Freshness data={data} closes={daysQuery.data ?? []} now={now} />

      <Panel>
        <Panel.Head title="Cart runs">
          <span className="faint text-[11.5px] ml-2.5">
            Under each stage: the time since the one before. The build makes Reach as each run ends from{" "}
            {fmtDate(data.reach_since)} on; the backfill job fills counted runs of the last 30 days that have none.
          </span>
        </Panel.Head>
        <Panel.Body tight>
          {data.stages.length === 0 ? (
            <EmptyState compact title="No cart runs yet">A build starts as soon as one ends.</EmptyState>
          ) : (
            <StageTable
              stages={data.stages}
              now={now}
              lag={lag}
              selected={chosen?.run_id ?? null}
              onSelect={setSelected}
              onRetry={canWrite && data.build_enabled ? retry : undefined}
            />
          )}
        </Panel.Body>
      </Panel>

      {chosen && (
        <Grid cols={2} gap="md">
          <BuildSteps stage={chosen} />
          <ReachHealth platform={platform} stage={chosen} />
        </Grid>
      )}

      <Grid cols={2} gap="md">
        <DayCloses closes={daysQuery.data ?? []} lastBackfill={data.last_backfill} backfillJobId={data.backfill_job_id} />
        {canWrite && <RebuildForm platform={platform} />}
      </Grid>
    </Stack>
  );
}

function Freshness({
  data,
  closes,
  now,
}: {
  data: AvailabilityPlatform;
  closes: { day: string; closed_at: string; fact_rows: number }[];
  now: Date;
}) {
  const newest = data.stages[0] ?? null;
  const built = data.stages.find((s) => s.reach_built_at !== null) ?? null;
  const yesterday = new Date(now.getTime() + 5.5 * 3600_000 - 86_400_000).toISOString().slice(0, 10);
  const yesterdayClose = closes.find((c) => c.day === yesterday) ?? null;
  return (
    <Grid cols={4} gap="md">
      <Stat
        surface
        label="newest cart run"
        value={newest ? fmtTime(newest.scrape_started_at) : "-"}
        small
        foot={
          <span className="faint">
            {newest === null ? "none yet" : newest.scrape_finished_at === null ? "scraping" : `ended ${ago(newest.scrape_finished_at)}`}
          </span>
        }
      />
      <Stat
        surface
        label="Reach built through"
        value={built ? fmtTime(built.scrape_started_at) : "-"}
        small
        tone={built === null && newest !== null ? "warn" : undefined}
        foot={<span className="faint">{built ? `built ${ago(built.reach_built_at)}` : "no run built yet"}</span>}
      />
      <Stat
        surface
        label={`yesterday (${fmtDate(yesterday)})`}
        value={yesterdayClose ? `closed ${fmtTime(yesterdayClose.closed_at)}` : "open"}
        small
        tone={yesterdayClose ? undefined : "warn"}
        foot={<span className="faint">{yesterdayClose ? `${fmtRows(yesterdayClose.fact_rows)} fact rows` : "the backfill closes it from 03:15"}</span>}
      />
      <Stat
        surface
        label="build job"
        value={<LastRun run={data.last_build} />}
        small
        foot={
          <span className="faint">
            {data.build_enabled ? "queued as each cart run ends" : "disabled"} -{" "}
            <Link to={`/jobs/${data.build_job_id}`}>job</Link>
          </span>
        }
      />
    </Grid>
  );
}

function LastRun({ run }: { run: JobLastRun | null }) {
  if (run === null) return <span className="faint">never run</span>;
  const live = run.status === "running" || run.status === "claimed" || run.status === "queued";
  return (
    <Link to={`/runs/${run.id}`} style={{ textDecoration: "none" }}>
      <Badge variant={toneVariant(statusTone(run.status))}>
        {live && <Dot live />}
        {run.status}
      </Badge>{" "}
      <span className="faint num text-[11px]">{ago(run.created_at)}</span>
    </Link>
  );
}

function StageCell({ at, from }: { at: string | null; from: string | null }) {
  if (at === null) return <span className="faint">-</span>;
  const delay = delaySeconds(from, at);
  return (
    <Stack gap="none">
      <span className="num">{fmtTime(at)}</span>
      {delay !== null && <span className="faint num text-[10.5px]">+{fmtDuration(delay)}</span>}
    </Stack>
  );
}

function StageTable({
  stages,
  now,
  lag,
  selected,
  onSelect,
  onRetry,
}: {
  stages: AvailabilityStageOut[];
  now: Date;
  lag: number;
  selected: string | null;
  onSelect: (runId: string) => void;
  onRetry?: () => void;
}) {
  return (
    <table className="tbl" style={{ tableLayout: "fixed", width: "100%" }}>
      <colgroup>
        <col style={{ width: 150 }} />
        <col />
        <col />
        <col />
        <col />
        <col style={{ width: 110 }} />
        <col style={{ width: 100 }} />
        <col style={{ width: 230 }} />
      </colgroup>
      <thead>
        <tr>
          <th>Cart run (start)</th>
          <th>Scraped</th>
          <th>Enriched</th>
          <th>Rolled up</th>
          <th>Reach built</th>
          <th className="r">Scrape to Reach</th>
          <th className="r">Fact rows</th>
          <th>State</th>
        </tr>
      </thead>
      <tbody>
        {stages.map((s) => {
          const state = stageState(s, now, lag);
          const total = delaySeconds(s.scrape_finished_at, s.reach_built_at);
          return (
            <tr
              key={s.run_id}
              onClick={() => onSelect(s.run_id)}
              aria-selected={s.run_id === selected}
              className={s.run_id === selected ? "row-selected" : state.kind === "failed" ? "row-crit" : undefined}
              style={{ cursor: "pointer" }}
            >
              <td>
                <Link to={`/runs/${s.cart_run_pk}`} className="num" onClick={(e) => e.stopPropagation()}>
                  {fmtTime(s.scrape_started_at)}
                </Link>
                {!s.full_scope && (
                  <div className="faint text-[10.5px]" title="Not every active store and brand: Qtrack does not count it">
                    partial scope
                  </div>
                )}
              </td>
              <td><StageCell at={s.scrape_finished_at} from={s.scrape_started_at} /></td>
              <td><StageCell at={s.enriched_at} from={s.scrape_finished_at} /></td>
              <td><StageCell at={s.rolled_up_at} from={s.enriched_at} /></td>
              <td><StageCell at={s.reach_built_at} from={s.rolled_up_at ?? s.enriched_at} /></td>
              <td className="r num">{total === null ? "-" : fmtDuration(total)}</td>
              <td className="r num">{fmtRows(s.fact_rows)}</td>
              <td>
                <Row gap="xs" className="flex-wrap">
                  <Badge variant={toneVariant(STATE_TONE[state.kind])}>
                    {state.kind === "scraping" && <Dot live />}
                    {state.kind === "failed"
                      ? `failed at ${stepLabel(state.step)}`
                      : state.kind === "late"
                        ? `late ${state.minutes} min`
                        : STATE_LABEL[state.kind]}
                  </Badge>
                  {state.kind === "failed" && onRetry && (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetry();
                      }}
                    >
                      Retry
                    </Button>
                  )}
                </Row>
                {state.kind === "failed" && (
                  <div className="text-crit text-[10.5px] truncate" title={state.error}>{state.error}</div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function BuildSteps({ stage }: { stage: AvailabilityStageOut }) {
  const runQuery = useGetRunV1RunsRunIdGet(stage.build_run_pk ?? "", {
    query: { enabled: stage.build_run_pk !== null, refetchInterval: 10_000 },
  });
  const run = runQuery.data;
  const snapshot = (run?.summary ?? run?.progress ?? null) as CartProgress | null;
  const ms = snapshot?.step_ms ?? {};
  const rows = snapshot?.step_rows ?? {};
  return (
    <Panel>
      <Panel.Head title="The build that took this run furthest">
        {run && (
          <Link to={`/runs/${run.id}`} className="ml-2.5 text-[12px]">
            {run.status} - {ago(run.created_at)}
          </Link>
        )}
      </Panel.Head>
      <Panel.Body tight>
        {stage.build_run_pk === null ? (
          <EmptyState compact title="No build has reached this run yet" />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Step</th>
                <th className="r">Time</th>
                <th className="r">Rows</th>
              </tr>
            </thead>
            <tbody>
              {stepsInOrder(ms).map((step) => (
                <tr key={step}>
                  <td>{stepLabel(step)}</td>
                  <td className="r num">{fmtDuration(ms[step] / 1000)}</td>
                  <td className="r num">{fmtInt(rows[step])}</td>
                </tr>
              ))}
              {snapshot?.failed_step && (
                <tr className="row-crit">
                  <td>{stepLabel(snapshot.failed_step)}</td>
                  <td className="r" colSpan={2}>failed</td>
                </tr>
              )}
              {snapshot?.ch_rows_read != null && (
                <tr>
                  <td className="faint">ClickHouse rows read</td>
                  <td className="r num" colSpan={2}>{fmtRows(snapshot.ch_rows_read)}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Panel.Body>
    </Panel>
  );
}

function ReachHealth({ platform, stage }: { platform: Platform; stage: AvailabilityStageOut }) {
  const healthQuery = useHealthV1AvailabilityHealthGet(
    { platform, run_id: stage.run_id },
    { query: { enabled: stage.reach_built_at !== null } },
  );
  const cities = [...(healthQuery.data ?? [])].sort((a, b) => b.cells - a.cells).slice(0, 12);
  return (
    <Panel>
      <Panel.Head title={`Reach health - run ${fmtTime(stage.scrape_started_at)}`} />
      <Panel.Body tight>
        {stage.reach_built_at === null ? (
          <EmptyState compact title="Reach not built for this run yet" />
        ) : healthQuery.isError ? (
          <ErrorState compact title="Could not read Reach health" message="Is ClickHouse up?" />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>City</th>
                <th className="r">Stores seen</th>
                <th className="r">Cells observed</th>
                <th className="r">Available</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((c) => {
                const share = cityShares(c);
                return (
                  <tr key={c.city}>
                    <td>{c.city}</td>
                    <td className="r num">
                      {c.stores_observed} / {c.stores_covering}
                    </td>
                    <td className="r num">{pct(share.observed)}</td>
                    <td className="r num">{pct(share.available)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel.Body>
    </Panel>
  );
}

function DayCloses({
  closes,
  lastBackfill,
  backfillJobId,
}: {
  closes: { day: string; closed_at: string; fact_rows: number; agg_rows: number }[];
  lastBackfill: JobLastRun | null;
  backfillJobId: string;
}) {
  return (
    <Panel>
      <Panel.Head title="Day closes">
        <span className="ml-2.5"><LastRun run={lastBackfill} /></span>
        <span className="faint text-[11.5px] ml-2.5">
          by the <Link to={`/jobs/${backfillJobId}`}>backfill job</Link>, from 03:15 IST
        </span>
      </Panel.Head>
      <Panel.Body tight>
        {closes.length === 0 ? (
          <EmptyState compact title="No day closed yet" />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Day</th>
                <th>Closed</th>
                <th className="r">Fact rows</th>
                <th className="r">Daily rows</th>
              </tr>
            </thead>
            <tbody>
              {closes.map((c) => (
                <tr key={c.day}>
                  <td>{fmtDate(c.day)}</td>
                  <td className="num">{fmtTime(c.closed_at)}</td>
                  <td className="r num">{fmtRows(c.fact_rows)}</td>
                  <td className="r num">{fmtRows(c.agg_rows)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel.Body>
    </Panel>
  );
}

function RebuildForm({ platform }: { platform: Platform }) {
  const notify = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState<RebuildMode>("close");
  const [firstDay, setFirstDay] = useState("");
  const [lastDay, setLastDay] = useState("");
  const rebuild = useRebuildV1AvailabilityRebuildPost<Error>({
    mutation: {
      onSuccess: (run) => {
        notify("Rebuild started");
        navigate(`/runs/${run.id}`);
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });
  const ready = firstDay !== "" && lastDay !== "";
  return (
    <Panel>
      <Panel.Head title="Rebuild days" />
      <Panel.Body>
        <Stack gap="sm">
          <FormField label="Mode" hint={MODES.find((m) => m.value === mode)?.hint}>
            <NativeSelect value={mode} onChange={(e) => setMode(e.currentTarget.value as RebuildMode)}>
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </NativeSelect>
          </FormField>
          <Row gap="sm">
            <FormField label="First day">
              <Input type="date" value={firstDay} onChange={(e) => setFirstDay(e.currentTarget.value)} />
            </FormField>
            <FormField label="Last day">
              <Input type="date" value={lastDay} onChange={(e) => setLastDay(e.currentTarget.value)} />
            </FormField>
          </Row>
          <span className="faint text-[11.5px]">
            One day per run, oldest first, so builds for new cart runs take their turn in between. At most 62 days.
          </span>
          <Row>
            <Button
              variant="primary"
              disabled={!ready}
              loading={rebuild.isPending}
              onClick={() =>
                rebuild.mutate({ data: { platform, mode, first_day: firstDay, last_day: lastDay } })
              }
            >
              Start rebuild
            </Button>
          </Row>
        </Stack>
      </Panel.Body>
    </Panel>
  );
}
